import { NUMERIC, Options, VALID_FORMATS } from './consts';
import { isValidDate, getValidLocale } from './helpers';

/**
 * `displayTime` takes a `Date` object and a format specifier, and returns a string representing time that is formatting according to format for the user's locale.
 * @param date - `Date` object or `number` to display as `string`.
 * @param options - options object can contain following fields:
 *   * `format` - either "default", "short" or "numeric".
 *   * `locale` - developer designated locale (this will take precedence over global value).
 *   * `utc` - converts `date` to UTC timezone if true. Defaults to false.
 * @returns a locale date string corresponding to current user's locale and format options.
 */
export const displayTime = (date: Date, options: Options = {}): string => {
  if (!isValidDate(date)) {
    throw new TypeError('The first argument must be a valid date.');
  }

  const { format, locale, utc } = options;

  if (!VALID_FORMATS.has(format)) {
    const supported = Array.from(VALID_FORMATS)
      .filter((f) => !!f)
      .map((f) => JSON.stringify(f));
    supported[2] = `or ${supported[2]}`;
    throw new TypeError(`The format must be either: ${supported.join(',')}.`);
  }

  const _options: Intl.DateTimeFormatOptions = {};
  _options.minute = NUMERIC;
  _options.hour = NUMERIC;

  if (utc) {
    _options.timeZone = 'UTC';
  }

  const localeId = getValidLocale(locale);
  return date.toLocaleTimeString(localeId, _options);
};
